import { Action, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { arrayMoveByIndex } from "array-move-multiple";
import differenceInSeconds from "date-fns/differenceInSeconds";
import { nanoid } from "nanoid";
import { createDoc } from "../system/thunks";
import { changeSourceToLink, getFakeStatus, getMainKeyword } from "./helpers";
import {
  archiveBriefRequested,
  archivedBriefsRequested,
  briefsRequested,
  briefUpdated,
  checkBriefStatus,
  createBrief,
  deleteBriefRequested,
  expandCompetitorOutline,
  getAllBriefData,
  getCompetitorsAsync,
  getQuestions,
  getSerpCompetitors,
  getStats,
  retryBrief,
  // getSerpCompetitorsTopics,
  singleBriefRequested,
  unarchiveBriefRequested
} from "./thunks";

import {
  BriefType,
  CBriefState,
  CompetitorType,
  OutlineType,
  SelectedTopicsType
} from "./types";

const initialLoadingState = {
  ["briefs/getSerpCompetitors"]: "idle",
  ["crawler/getCompetitors"]: "idle",
  ["briefs/getQuestionsQuora"]: "idle",
  ["briefs/getStats"]: "idle",

  ["briefs/getQuestionsReddit"]: "idle",
  ["briefs/singleObjectRequested"]: "idle",
  ["briefs/briefAsync"]: "idle",

};

const initialState = {
  entities: [],
  loading: {
    ["briefs/create"]: "idle",
    ["briefs/requested"]: "idle",
  },
  currentBriefId: "",
  selectedOutlines: [],
  isGdocCreating: false,
  limit: 25,
  page: 0,
  total: 0,
  project: "",
  paginationTokens: {
    0: {
      id: "",
      createdAt: "",
    },
  },
  archivedEntities: [],
  archivedLimit: 25,
  archivedTotal: 0,
  archivedPaginationTokens: {
    0: {
      id: "",
      createdAt: "",
    },
  },
  archivedPage: 0,
} as CBriefState;

const getCurrentBrief = (state: CBriefState) => {
  const briefIndex = state.entities.findIndex(
    (brief) => brief.id === state.currentBriefId
  );
  const currentBrief = state.entities[briefIndex];
  return currentBrief;
};

const getSelectedOutline = (state: CBriefState, id: string) => {
  const currentBrief = getCurrentBrief(state);

  const selectedIndex = currentBrief.selectedOutlines.findIndex(
    (item) => item.id === id
  );
  return currentBrief.selectedOutlines[selectedIndex];
};

const briefSlice = createSlice({
  name: "briefs",
  initialState,
  reducers: {
    reset: () => initialState,
    briefLoaded: (state, action: PayloadAction<string>) => {
      state.currentBriefId = action.payload;
      state.selectedOutlines = [];
      state.loading[action.payload] = initialLoadingState;
    },
    competitorTitleSelected: (state, action: PayloadAction<string>) => {
      const currentBrief = getCurrentBrief(state);
      const index = currentBrief.competitors.findIndex(
        (item) => item.title === action.payload
      );

      currentBrief.competitors[index].titleSelected =
        !currentBrief.competitors[index].titleSelected;
    },
    outlineSelected: (state, action: PayloadAction<OutlineType>) => {
      const currentBrief = getCurrentBrief(state);
      const lastSelectedIndex = currentBrief.selectedOutlines.findIndex(
        ({ id }) =>
          id ===
          (state.selectedOutlines[state.selectedOutlines.length] ??
            state.selectedOutlines[0])
      );

      const outlineIndex = currentBrief.selectedOutlines.findIndex(
        (item) => item.id === action.payload?.id
      );
      const isAlreadyAdded = outlineIndex >= 0;

      if (!isAlreadyAdded && lastSelectedIndex === -1) {
        currentBrief.selectedOutlines.push({
          ...action.payload,
          index: currentBrief.selectedOutlines.length,
        });
      } else if (!isAlreadyAdded && lastSelectedIndex >= 0) {
        currentBrief.selectedOutlines.splice(lastSelectedIndex + 1, 0, {
          ...action.payload,
          index: lastSelectedIndex + 1,
        });
        state.selectedOutlines = [action.payload.id];
      } else {
        state.selectedOutlines.splice(lastSelectedIndex, 1);
        currentBrief.selectedOutlines.splice(outlineIndex, 1);
      }
    },
    insightdAdded: (state, action: PayloadAction<any>) => {
      const currentBrief = getCurrentBrief(state);
      currentBrief.insights = action.payload;
    },

    briefOutlineSelected: (
      state,
      action: PayloadAction<{ id: string; isCtrl?: boolean; isShift?: boolean }>
    ) => {
      const index = state.selectedOutlines.findIndex(
        (id) => id === action.payload.id
      );
      if (action.payload.isCtrl) {
        if (index >= 0) {
          state.selectedOutlines.splice(index, 1);
        } else {
          state.selectedOutlines.push(action.payload.id);
        }
      } else if (action.payload.isShift) {
        const currentBrief = getCurrentBrief(state);
        const first = state.selectedOutlines[state.selectedOutlines.length - 1];
        const last = action.payload.id;
        const firstIdx = currentBrief.selectedOutlines.findIndex(
          (outline) => outline.id === first
        );
        const lastIdx = currentBrief.selectedOutlines.findIndex(
          (outline) => outline.id === last
        );
        const lastSliceIdx = lastIdx > firstIdx ? lastIdx : firstIdx;
        const firstSliceIdx = lastIdx > firstIdx ? firstIdx : lastIdx;
        const idxArray = currentBrief.selectedOutlines.slice(
          firstSliceIdx,
          lastSliceIdx + 1
        );
        idxArray.forEach(({ id }) => {
          if (!state.selectedOutlines.includes(id)) {
            state.selectedOutlines.push(id);
          }
        });
      } else if (index >= 0) {
        state.selectedOutlines.splice(index, 1);
      } else {
        state.selectedOutlines = [action.payload.id];
      }
    },
    outlinePositionChanged: (
      state,
      {
        payload: { outlines, move },
      }: PayloadAction<{ outlines: string[]; move: number }>
    ) => {
      const currentBrief = getCurrentBrief(state);
      const outlineList = [...currentBrief.selectedOutlines];
      const indexes = [];
      outlines.forEach((id) => {
        const oldIndex = currentBrief.selectedOutlines.findIndex(
          (outline) => outline.id === id
        );
        indexes.push(oldIndex);
      });
      currentBrief.selectedOutlines = arrayMoveByIndex(
        outlineList,
        indexes,
        indexes[0] + move
      );
    },
    indentationChanged: (
      state,
      action: PayloadAction<{ move: number; id: string }>
    ) => {
      const selectedOutline = getSelectedOutline(state, action.payload.id);
      const [tag, currentIndentation] = selectedOutline.tag.split("");

      const newIndentation = Number(currentIndentation) + action.payload.move;

      if (newIndentation > 1 && newIndentation <= 4) {
        selectedOutline.tag = `${tag}${newIndentation}`;
      }
    },
    newLinkAdded: (state, action: PayloadAction<{ id: string }>) => {
      const selectedOutline = getSelectedOutline(state, action.payload.id);
      if (selectedOutline.urls) {
        selectedOutline.urls.push("");
      }
    },
    tagTypeChanged: (
      state,
      action: PayloadAction<{ type: "H" | "P"; id: string }>
    ) => {
      const selectedOutline = getSelectedOutline(state, action.payload.id);
      const indent = selectedOutline.tag.slice(1) || 2;
      if (selectedOutline?.tag) {
        selectedOutline.tag = `${action.payload.type}${indent}`;
      }
    },
    newOutlineAdded: (
      state,
      action: PayloadAction<{
        text: string;
        urls?: string[];
        tag?: string;
        id?: string;
      }>
    ) => {
      const currentBrief = getCurrentBrief(state);
      const lastSelectedOutlineIndex = currentBrief.selectedOutlines.findIndex(
        (item) =>
          item.id === state.selectedOutlines[state.selectedOutlines.length - 1]
      );

      const newOutline = {
        id: action.payload.id ?? nanoid(),
        text: action.payload.text,
        tag: action.payload.tag ?? "H2",
        urls: action.payload.urls ?? [""],
        takeaways: [],
      };

      if (lastSelectedOutlineIndex !== -1) {
        currentBrief.selectedOutlines.splice(lastSelectedOutlineIndex + 1, 0, {
          ...newOutline,
          index: lastSelectedOutlineIndex + 1,
        });
      } else {
        currentBrief.selectedOutlines.push(newOutline);
      }
    },
    multipleOutlinesAdded: (
      state,
      action: PayloadAction<{
        text: string;
        urls?: string[];
        tag?: string;
        takeaways?: string[];
      }[]>
    ) => {
      const currentBrief = getCurrentBrief(state);
      
      action.payload.forEach((outlineData) => {
        const newOutline = {
          id: nanoid(),
          text: outlineData.text,
          tag: outlineData.tag ?? "H2",
          urls: outlineData.urls ?? [""],
          takeaways: outlineData.takeaways ?? [],
        };
        
        currentBrief.selectedOutlines.push(newOutline);
      });
    },
    multipleOutlinesAddedAtPosition: (
      state,
      action: PayloadAction<{
        outlines: {
          id?: string;
          text: string;
          urls?: string[];
          tag?: string;
          takeaways?: string[];
        }[];
        insertAfterIndex?: number;
      }>
    ) => {
      const currentBrief = getCurrentBrief(state);
      const { outlines: newOutlines, insertAfterIndex } = action.payload;
      
      // If no insert position specified, find the last selected outline position
      let insertPosition = insertAfterIndex;
      if (insertPosition === undefined) {
        const lastSelectedOutlineIndex = state.selectedOutlines.length > 0 
          ? currentBrief.selectedOutlines.findIndex(
              (item) => item.id === state.selectedOutlines[state.selectedOutlines.length - 1]
            )
          : -1;
        
        insertPosition = lastSelectedOutlineIndex >= 0 ? lastSelectedOutlineIndex : currentBrief.selectedOutlines.length - 1;
      }
      
      const outlinesData = newOutlines.map((outlineData) => ({
        id: outlineData.id ?? nanoid(),
        text: outlineData.text,
        tag: outlineData.tag ?? "H2",
        urls: outlineData.urls ?? [""],
        takeaways: outlineData.takeaways ?? [],
      }));
      
      // Insert outlines at the specified position
      currentBrief.selectedOutlines.splice(insertPosition + 1, 0, ...outlinesData);
    },
    outlineEdited: (
      state,
      action: PayloadAction<{ text: string; id: string; urls?: string[] }>
    ) => {
      const currentBrief = getCurrentBrief(state);
      const selectedIndex = currentBrief.selectedOutlines.findIndex(
        (item) => item.id === action.payload.id
      );

      currentBrief.selectedOutlines[selectedIndex].text = action.payload.text;
      if (action.payload.urls) {
        currentBrief.selectedOutlines[selectedIndex].urls = action.payload.urls;
      }
    },
    customTopicAdded: (state, action: PayloadAction<SelectedTopicsType>) => {
      const currentBrief = getCurrentBrief(state);
      currentBrief.selectedTopics.push(action.payload);
    },
    topicSelected: (state, action: PayloadAction<SelectedTopicsType>) => {
      const currentBrief = getCurrentBrief(state);

      const index = currentBrief.selectedTopics.findIndex(
        (item) => item.keyword === action.payload.keyword
      );

      if (index === -1) {
        currentBrief.selectedTopics.push(action.payload);
      } else {
        currentBrief.selectedTopics.splice(index, 1);
      }
    },
    allTopicsSelected: (state, action: PayloadAction<SelectedTopicsType[]>) => {
      const currentBrief = getCurrentBrief(state);
      currentBrief.selectedTopics = [
        ...currentBrief.selectedTopics,
        ...action.payload,
      ].filter(
        (value, index, self) =>
          index === self.findIndex((t) => t.keyword === value.keyword)
      );
    },
    removeAllTopics: (state) => {
      const currentBrief = getCurrentBrief(state);
      currentBrief.selectedTopics = [];
    }
    ,
    topicIgnored: (state, action: PayloadAction<SelectedTopicsType>) => {
      const currentBrief = getCurrentBrief(state);
      const index = currentBrief.selectedTopics.findIndex(
        (item) => item.keyword === action.payload.keyword
      );
      if (index !== -1) {
        currentBrief.selectedTopics.splice(index, 1);
      }
    },
    checkFakeStatuses: (state, action) => {
      for (const brief of state.entities) {
        const timeDiff = differenceInSeconds(
          new Date(action.payload),
          new Date(brief.createdAt)
        );
        brief.fakeStatus = getFakeStatus(timeDiff);
      }
    },
    setFakeCreatedAt: (state, action) => {
      const currentBrief = state.entities.find(
        ({ id }) => id === action.payload.id
      );
      currentBrief.createdAt = action.payload.createdAt;
      const timeDiff = differenceInSeconds(
        new Date(action.payload.createdAt),
        new Date(currentBrief.createdAt)
      );
      currentBrief.fakeStatus = getFakeStatus(timeDiff);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createBrief.pending, (state) => {
        state.loading["briefs/create"] = "pending";
      })
      .addCase(
        createBrief.fulfilled,
        (state, action: PayloadAction<BriefType>) => {
          state.loading["briefs/create"] = "finished";
          if (action.payload?.id) {
            state.loading[action.payload.id] = initialLoadingState;
            state.entities = [
              {
                ...action.payload,
                status: "Preparing empty brief",
                jobId: action.payload.jobId || "",
                fakeStatus: "Pending",
              },
              ...state.entities,
            ];
          }
        }
      )
      .addCase(
        getSerpCompetitors.pending,
        (
          state,
          action: PayloadAction<
            CompetitorType[],
            string,
            { arg: { id: string } }
          >
        ) => {
          state.loading[action.meta.arg.id] = {
            ...state.loading[action.meta.arg.id],
            "briefs/getCompetitors": "pending",
          };
          const currentBrief = state.entities.find(
            (brief) => brief.id === action.meta.arg.id
          );
          currentBrief.status = "Extracting Keywords";
        }
      )
      .addCase(
        getSerpCompetitors.fulfilled,
        (
          state,
          action: { meta: { arg: { id: string } } } & PayloadAction<{
            data: { result: [{ items: CompetitorType[] }]; id: string };
          }>
        ) => {
          state.loading[action.meta.arg.id][
            action.type.replace("/fulfilled", "")
          ] = "finished";

          const currentBrief = state.entities.find(
            (brief) => brief.id === action.meta.arg.id
          );

          if (action.payload?.data && action.payload?.data?.result) {
            currentBrief.competitors = action.payload.data?.result[0]?.items.map(
              (competitor) => ({
                ...competitor,
                titleSelected: false,
              })
            );
            currentBrief.jobId =
              (currentBrief.jobId || action.payload?.data.id) ?? "";
          }
        }
      )

      .addCase(
        getCompetitorsAsync.pending,
        (
          state,
          action: PayloadAction<
            CompetitorType[],
            string,
            { arg: { id: string } }
          >
        ) => {
          state.loading[action.meta.arg.id] = {
            ...state.loading[action.meta.arg.id],
            "briefs/getCompetitorsAsync": "pending",
          };
          const currentBrief = state.entities.find(
            (brief) => brief.id === action.meta.arg.id
          );
          if (currentBrief.status !== "Finished") {
            currentBrief.status = "Filtering Relevant Keywords";
          }
        }
      )
      .addCase(getCompetitorsAsync.fulfilled, (state, action) => {
        const currentBrief = state.entities.find(
          (brief) => brief.id === action.meta.arg.id
        );

        if (action.payload?.status !== "done") {
          return;
        }

        state.loading[action.meta.arg.id][
          action.type.replace("/fulfilled", "")
        ] = "finished";

        if (currentBrief.jobId) {
          currentBrief.jobId = currentBrief.jobId ?? action.payload.id;
        }
      })

      .addCase(
        briefUpdated.pending,
        (state, action: { meta: { arg: any }; type: string }) => {
          state.loading[action.meta.arg.id][
            action.type.replace("/pending", "")
          ] = "pending";
          const index = state.entities.findIndex(
            (brief) => brief.id === action.meta.arg.id
          );
          const title = action.meta.arg.title;
          const description = action.meta.arg.description;
          const isPublic = action.meta.arg.isPublic;
          const headings = action.meta.arg.headings;
          const images = action.meta.arg.images;
          const wordCount = action.meta.arg.wordCount;

          if (title) {
            state.entities[index].title = title;
          }
          if (description) {
            state.entities[index].description = description;
          }
          if (typeof isPublic !== "undefined") {
            state.entities[index].isPublic = isPublic;
          }
          state.entities[index].headings = headings;
          state.entities[index].images = images;
          state.entities[index].wordCount = wordCount;
        }
      )
      .addCase(briefUpdated.fulfilled, (state, action: PayloadAction<any>) => {
        if (action?.payload?.id) {
          state.loading[action.payload.id][
            action.type.replace("/fulfilled", "")
          ] = "finished";
          const index = state.entities.findIndex(
            (brief) => brief.id === action.payload.id
          );
          state.entities[index].updatedAt = action.payload.updatedAt;
          state.entities[index].showHeadingsDialog =
            action.payload.showHeadingsDialog;

        }
      })
      .addCase(briefsRequested.pending, (state) => {
        state.loading["briefs/requested"] = "pending";
      })
      .addCase(briefsRequested.fulfilled, (state, action) => {
        state.loading["briefs/requested"] = "finished";
        state.currentBriefId = "";
        state.entities = action.payload?.briefs?.map((brief) => {
          const timeDiff = differenceInSeconds(
            new Date(),
            new Date(brief.createdAt)
          );
          return {
            ...brief,
            status: "Idle",
            fakeStatus: getFakeStatus(timeDiff),
          };
        });
        state.limit = action.payload?.limit ?? 50;
        state.total = action.payload?.totalNumberOfBriefs;
        state.paginationTokens[state.page + 1] = action.payload?.paginationToken;
        state.page++;
      })
      .addCase(archivedBriefsRequested.fulfilled, (state, action) => {
        state.loading["briefs/requested"] = "finished";
        state.currentBriefId = "";
        state.archivedEntities = action.payload?.briefs?.map((brief) => {
          const timeDiff = differenceInSeconds(
            new Date(),
            new Date(brief.createdAt)
          );
          return {
            ...brief,
            status: "Idle",
            fakeStatus: getFakeStatus(timeDiff),
          };
        });
        state.archivedLimit = action.payload?.limit ?? 50;
        state.archivedTotal = action.payload?.totalNumberOfBriefs;
        state.archivedPaginationTokens[state.page + 1] =
          action.payload?.paginationToken;
        state.archivedPage++;
      })
      .addCase(
        getQuestions.pending,
        (
          state,
          action: PayloadAction<
            CompetitorType[],
            string,
            { arg: { briefId: string; keyword: string; site: string } }
          >
        ) => {
          const currentBrief = state.entities.find(
            (brief) => brief.id === action.meta.arg.briefId
          );
          currentBrief.status = "Analyzing Competition";

          switch (action.meta.arg.site) {
            case "quora.com":
              state.loading[action.meta.arg.briefId][
                "briefs/getQuestionsQuora"
              ] = "pending";
              return;
            case "reddit.com":
              state.loading[action.meta.arg.briefId][
                "briefs/getQuestionsReddit"
              ] = "pending";
              return;
          }
        }
      )
      .addCase(
        getQuestions.fulfilled,
        (
          state,
          action: PayloadAction<
            {
              data: {
                title: string;
                vol?: number;
                id: string;
                selected: boolean;
              }[];
            },
            string,
            { arg: { keyword: string; briefId: string; site: string } }
          >
        ) => {
          state.loading[action.meta.arg.briefId][
            action.type.replace("/fulfilled", "")
          ] = "finished";
          const currentBrief = state.entities.find(
            (brief) => brief.id === action.meta.arg.briefId
          );

          switch (action.meta.arg.site) {
            case "quora.com":
              currentBrief.relatedQuestionsQuora = action.payload.data;
              state.loading[action.meta.arg.briefId][
                "briefs/getQuestionsQuora"
              ] = "finished";
              return;

            case "reddit.com":
              currentBrief.relatedQuestionsReddit = action.payload.data;
              currentBrief.status = "Finished";
              state.loading[action.meta.arg.briefId][
                "briefs/getQuestionsReddit"
              ] = "finished";
              return;

            default:
              break;
          }
        }
      )
      .addCase(
        getStats.pending,
        (
          state,
          action: PayloadAction<any, string,
            { arg: { briefId: string; keyword: string } }
          >
        ) => {
          const currentBrief = state.entities.find(
            (brief) => brief.id === action.meta.arg.briefId
          );
          currentBrief.status = "Getting Statistics";
          state.loading[action.meta.arg.briefId][
            "briefs/getStats"
          ] = "pending";
        }
      )
      .addCase(
        getStats.fulfilled,
        (
          state,
          action: PayloadAction<any, string,
            { arg: { briefId: string; keyword: string } }
          >
        ) => {

          state.loading[action.meta.arg.briefId][
            action.type.replace("/fulfilled", "")
          ] = "finished";
          const currentBrief = state.entities.find(
            (brief) => brief.id === action.meta.arg.briefId
          );
          currentBrief.statistics = action.payload.data.Attributes.statistics;
          currentBrief.status = "Finished";
        }
      )
      .addCase(
        getStats.rejected,
        (
          state,
          action: PayloadAction<any, string,
            { arg: { briefId: string; keyword: string } }
          >
        ) => {
          state.loading[action.meta.arg.briefId][
            action.type.replace("/rejected", "")
          ] = "finished";
          const currentBrief = state.entities.find(
            (brief) => brief.id === action.meta.arg.briefId
          );
          currentBrief.status = "Finished";
        }
      )
      .addCase(
        expandCompetitorOutline.pending,
        (
          state,
          action: PayloadAction<CompetitorType[], string, { arg: number }>
        ) => {
          state.loading[action.meta.arg][action.type.replace("/pending", "")] =
            "pending";
        }
      )
      .addCase(
        expandCompetitorOutline.fulfilled,
        (
          state,
          action: PayloadAction<{ headings: OutlineType[]; id: number }>
        ) => {
          const currentBrief = getCurrentBrief(state);
          state.loading[action.payload.id][
            action.type.replace("/fulfilled", "")
          ] = "finished";

          const competitorIndex = currentBrief.competitors.findIndex(
            (competitor) => competitor.rank_absolute === action.payload.id
          );

          if (competitorIndex > -1) {
            currentBrief.competitors[competitorIndex].headings =
              action.payload.headings;
            currentBrief.competitors[competitorIndex].isExtracted = true;
          }
        }
      )
      .addCase(
        singleBriefRequested.pending,
        (
          state,
          action: PayloadAction<
            CompetitorType[],
            string,
            { arg: { id: string } }
          >
        ) => {
          state.loading[action.meta.arg.id][
            action.type.replace("/pending", "")
          ] = "pending";
        }
      )
      .addCase(
        getAllBriefData.rejected,
        (state, action: Action & { meta: { arg: string } }) => {
          let loadingState =
            state.loading[action.meta.arg]?.[
            action.type.replace("/rejected", "")
            ];
          if (loadingState) {
            loadingState = "finished";
          }
        }
      )
      .addCase(
        getAllBriefData.pending,
        (
          state,
          action: PayloadAction<CompetitorType[], string, { arg: string }>
        ) => {
          state.loading[action.meta.arg] = {
            ...initialLoadingState,
            ...state.loading[action.meta.arg],
          };
          state.loading[action.meta.arg][action.type.replace("/pending", "")] =
            "pending";
        }
      )
      .addCase(
        getAllBriefData.fulfilled,
        (
          state,
          action: PayloadAction<
            {
              data: {
                competitors: CompetitorType[];
                questions: {
                  quora: {
                    title: string;
                    vol?: number;
                    id: string;
                    selected: boolean;
                  }[];
                  reddit: {
                    title: string;
                    vol?: number;
                    id: string;
                    selected: boolean;
                  }[];
                };
              };
            },
            string,
            { arg: string }
          >
        ) => {
          state.loading[action.meta.arg] = {
            ...initialLoadingState,
            ...state.loading[action.meta.arg],
          };
          state.loading[action.meta.arg][
            action.type.replace("/fulfilled", "")
          ] = "finished";
          let currentBrief = state.entities.find(
            (brief) => brief.id === action.meta.arg
          );
          //  also look for the brief in archivedEntities
          if (!currentBrief) {
            const archivedBrief = state.archivedEntities.find(
              (brief) => brief.id === action.meta.arg
            );
            if (archivedBrief) {
              currentBrief = archivedBrief;
            }
          }

          currentBrief.status = "Finished";

          state.loading[action.meta.arg]["briefs/getQuestionsReddit"] =
            "finished";
          state.loading[action.meta.arg]["briefs/getQuestionsQuora"] =
            "finished";

          currentBrief.competitors = action.payload.data.competitors;
          currentBrief.relatedQuestionsQuora =
            action.payload.data.questions.quora;
          currentBrief.relatedQuestionsReddit =
            action.payload.data.questions.reddit;

          if (!(currentBrief.mainKeywordSearchVolume > 0)) {
            currentBrief.mainKeywordSearchVolume = getMainKeyword({
              competitors: action.payload.data.competitors,
              keyword: currentBrief.keyword,
            });
          }

          for (const result of action.payload.data.competitors) {
            const competitorIndex = currentBrief.competitors.findIndex(
              (competitor) => competitor.url === result.url
            );
            if (competitorIndex !== -1) {
              currentBrief.competitors[competitorIndex].headings =
                result.headings;
              currentBrief.competitors[competitorIndex].wordCount =
                result.wordCount;
              currentBrief.competitors[competitorIndex].h1text = result.h1text;
            }
          }
        }
      )
      .addCase(
        singleBriefRequested.fulfilled,
        (state, action: PayloadAction<BriefType>) => {
          state.loading[action.payload.id][
            action.type.replace("/fulfilled", "")
          ] = "finished";
          const isExistingBrief = state.entities.findIndex(
            (brief) => brief.id === action.payload.id
          );
          if (isExistingBrief === -1) {
            state.entities.push({
              ...action.payload,
              selectedOutlines: changeSourceToLink(
                action.payload.selectedOutlines
              ),
            });
          }





        }
      )
      .addCase(checkBriefStatus.fulfilled, (state, action) => {
        const index = state.entities.findIndex(brief => brief.id === action.payload.id);
        if (index !== -1) {
          if (action.payload.stepRemains != null) {
            state.entities[index] = action.payload;
          }
        }
      })
      .addCase(retryBrief.pending, (state, action) => {
        const index = state.entities.findIndex(brief => brief.id === action.meta.arg.id);
        if (index !== -1) {
          state.entities[index] =
          {
            ...state.entities[index],
            retry: false,
            stepRemains: 7,
            createdAt: new Date().toISOString(),



          };
        }
      }
      )
      .addCase(retryBrief.fulfilled, (state, action) => {
        const index = state.entities.findIndex(brief => brief.id === action.payload?.data?.id);
        if (index !== -1) {
          if (action.payload.stepRemains != null) {
            state.entities[index] = action.payload?.data;
          }
        }
      })
      .addCase(
        createDoc.pending,
        (state) => {
          state.isGdocCreating = true;
        }
      )
      .addCase(
        createDoc.fulfilled,
        (state, action: PayloadAction<{ success: boolean }>) => {
          if (action.payload?.success) {
            state.isGdocCreating = false;
          }
        }
      )
      .addCase(
        createDoc.rejected,
        (state) => {
          state.isGdocCreating = false;
        }
      )
      .addCase(
        archiveBriefRequested.fulfilled,
        (state, action: PayloadAction & { meta: { arg: { id: string } } }) => {
          const currentBrief = state.entities.find(
            (element) => element.id === action.meta.arg.id
          );

          state.archivedEntities = [currentBrief, ...state.archivedEntities];

          state.entities = state.entities.filter(
            (element) => element.id !== action.meta.arg.id
          );
        }
      )
      .addCase(
        unarchiveBriefRequested.fulfilled,
        (state, action: PayloadAction & { meta: { arg: { id: string } } }) => {
          const currentBrief = state.archivedEntities.find(
            (element) => element.id === action.meta.arg.id
          );
          state.entities = [currentBrief, ...state.entities];

          state.archivedEntities = state.archivedEntities.filter(
            (element) => element.id !== action.meta.arg.id
          );
        }
      )
      .addCase(
        deleteBriefRequested.fulfilled,
        (state, action: PayloadAction & { meta: { arg: { id: string } } }) => {
          const index = state.entities.findIndex(
            (element) => element.id === action.meta.arg.id
          );
          const archiveIndex = state.archivedEntities.findIndex(
            (element) => element.id === action.meta.arg.id
          );
          if (index !== -1) {
            state.entities.splice(index, 1);
          }
          if (archiveIndex !== -1) {
            state.archivedEntities.splice(archiveIndex, 1);
          }
        }
      );
  },
});

export const {
  reset,
  briefLoaded,
  competitorTitleSelected,
  insightdAdded,
  outlineSelected,
  briefOutlineSelected,
  outlinePositionChanged,
  indentationChanged,
  tagTypeChanged,
  newOutlineAdded,
  multipleOutlinesAdded,
  multipleOutlinesAddedAtPosition,
  outlineEdited,
  topicSelected,
  removeAllTopics,
  customTopicAdded,
  topicIgnored,
  allTopicsSelected,
  newLinkAdded,
  checkFakeStatuses,
  setFakeCreatedAt,
} = briefSlice.actions;

export * from "./selectors";
export default briefSlice.reducer;
